import React, { useState, useEffect } from 'react';
import { authAPI } from '../../services/api';
import type { UserRole, User } from '../../contexts/AuthContext';
import { useAuth } from '../../contexts/AuthContext';
import DateInput from '../../components/DateInput';
import DocumentInput from '../../components/DocumentInput';
import EmailInput from '../../components/EmailInput';
import PhoneInput from '../../components/PhoneInput';
import PasswordInput from '../../components/PasswordInput';
import PhoneLink from '../../components/PhoneLink';
import { useI18n } from '../../contexts/I18nContext';
import { useCompany } from '../../contexts/CompanyContext';
import { fetchCompanyUsageStats } from '../../services/companyCustomizationApi';
import { useToast } from '../../components/Toast';

interface CreateUserData {
  email: string;
  password: string;
  name: string;
  role: UserRole;
  phone?: string;
  document?: string;
  birth_date?: string;
}

interface EditUserData {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  phone?: string;
  document?: string;
  birth_date?: string;
  password?: string;
}

export const UsersManagement = () => {
  const { t } = useI18n();
  const { user: currentUser } = useAuth();
  const { currentCompany } = useCompany();
  const { showError } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [filterRole, setFilterRole] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    loadUsers();
  }, [filterRole, filterStatus]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      if (filterRole) params.append('role', filterRole);
      if (filterStatus) params.append('status', filterStatus);
      
      const response = await authAPI.get(`/admin/users?${params}`);
      setUsers(response.data.users || []);
    } catch (error: any) {
      setError(t('errorLoadingUsers'));
    } finally {
      setLoading(false);
    }
  };

  const createUser = async (userData: CreateUserData) => {
    try {
      setError('');
      
      // Obter company_id do usuário logado ou buscar empresa padrão
      let companyId = currentUser?.company_id;
      
      if (!companyId) {
        // Tentar buscar do localStorage
        companyId = localStorage.getItem('default_company_id') || undefined;
        
        // Se ainda não tiver, buscar da API
        if (!companyId) {
          try {
            const companyResponse = await authAPI.get('/auth/default-company');
            companyId = companyResponse.data.company_id;
            if (companyId) {
              localStorage.setItem('default_company_id', companyId);
            }
          } catch (apiError) {
            console.error('Erro ao buscar empresa padrão:', apiError);
            setError(t('users.errorGetCompany'));
            return;
          }
        }
      }
      
      if (!companyId) {
        setError(t('users.errorCompany'));
        return;
      }

      // Verificar limite de usuários antes de criar
      if (currentCompany && currentCompany.id === companyId) {
        try {
          const stats = await fetchCompanyUsageStats(companyId);
          const userStat = stats.find(s => s.resource_type === 'users');
          if (userStat) {
            if (userStat.is_exceeded) {
              showError(`Limite de usuários excedido! Limite: ${userStat.limit}, Atual: ${userStat.current}. Entre em contato com o administrador do sistema.`);
              setError(`Limite de usuários excedido! Limite: ${userStat.limit}, Atual: ${userStat.current}.`);
              return;
            }
            if (userStat.percentage >= 90) {
              showError(`Atenção: Limite de usuários próximo! Você está usando ${userStat.percentage.toFixed(1)}% do limite (${userStat.current}/${userStat.limit}).`);
              // Continuar mesmo assim, apenas avisar
            }
          }
        } catch (err) {
          console.warn('Não foi possível verificar limite de usuários:', err);
          // Continuar mesmo se não conseguir verificar
        }
      }
      
      // Incluir company_id na requisição
      const payload = {
        ...userData,
        company_id: companyId
      };
      
      await authAPI.post('/admin/users', payload);
      setShowCreateModal(false);
      loadUsers();
    } catch (error: any) {
      setError(t('errorCreatingUser') + ': ' + (error.response?.data?.error || error.message));
    }
  };

  const updateUser = async (userData: EditUserData) => {
    try {
      setError('');
      await authAPI.put(`/admin/users/${userData.id}`, userData);
      setShowEditModal(false);
      setEditingUser(null);
      loadUsers();
    } catch (error: any) {
      setError(t('errorUpdatingUser') + ': ' + (error.response?.data?.error || error.message));
    }
  };

  const updateUserRole = async (userId: string, newRole: UserRole) => {
    try {
      setError('');
      await authAPI.put(`/admin/users/${userId}/role`, { role: newRole });
      loadUsers();
    } catch (error: any) {
      setError(t('errorUpdatingRole') + ': ' + (error.response?.data?.error || error.message));
    }
  };

  const deleteUser = async (userId: string) => {
    if (!confirm(t('confirmDelete'))) return;
    
    try {
      setError('');
      await authAPI.delete(`/admin/users/${userId}`);
      loadUsers();
    } catch (error: any) {
      setError('Erro ao excluir usuário: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEditClick = (user: User) => {
    setEditingUser(user);
    setShowEditModal(true);
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ margin: 0 }}>{t('userManagement')}</h1>
        <button 
          onClick={() => setShowCreateModal(true)}
          style={{
            padding: '10px 20px',
            backgroundColor: '#6366f1',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          + {t('createNewUser')}
        </button>
      </div>

      {error && (
        <div style={{
          padding: '12px',
          backgroundColor: '#fee2e2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          color: '#dc2626',
          marginBottom: '20px',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      {/* Filtros */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px'
      }}>
        <div style={{ flex: 1 }}>
          <label htmlFor="user-filter-role" style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
            {t('filterByRole')}
          </label>
          <select
            id="user-filter-role"
            name="filter-role"
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          >
            <option value="">{t('allRoles')}</option>
            <option value="admin">{t('administrator')}</option>
            <option value="supervisor">{t('supervisor')}</option>
            <option value="employee">{t('employee')}</option>
            <option value="client">{t('client')}</option>
          </select>
        </div>

        <div style={{ flex: 1 }}>
          <label htmlFor="user-filter-status" style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
            {t('filterByStatus')}
          </label>
          <select
            id="user-filter-status"
            name="filter-status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          >
            <option value="">{t('allStatus')}</option>
            <option value="active">{t('active')}</option>
            <option value="inactive">{t('inactive')}</option>
          </select>
        </div>
      </div>

      {/* Tabela de Usuários */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          {t('loading')}...
        </div>
      ) : users.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          {t('errorLoadingUsers')}
        </div>
      ) : (
        <div style={{ overflowX: 'auto', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              <tr>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>{t('name')}</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>{t('email')}</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>{t('role')}</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>{t('phone')}</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>{t('status')}</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>{t('lastLogin')}</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500' }}>{user.name || '-'}</td>
                  <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>{user.email}</td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      backgroundColor: getRoleColor(user.role),
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {getRoleLabel(user.role)}
                    </span>
                  </td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>
                    <PhoneLink phone={user.phone || ''} showIcon={false} />
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      backgroundColor: user.is_active ? '#10b981' : '#6b7280',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {user.is_active ? t('active') : t('inactive')}
                    </span>
                  </td>
                  <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                    {user.last_login_at ? new Date(user.last_login_at).toLocaleString('pt-BR') : t('never')}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button 
                        onClick={() => handleEditClick(user)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        {t('edit')}
                      </button>
                      <button 
                        onClick={() => deleteUser(user.id)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        {t('delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de Criação */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onCreate={createUser}
        />
      )}

      {/* Modal de Edição */}
      {showEditModal && editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => {
            setShowEditModal(false);
            setEditingUser(null);
          }}
          onUpdate={updateUser}
        />
      )}
    </div>
  );
};

// Modal de Criação de Usuário
interface CreateUserModalProps {
  onClose: () => void;
  onCreate: (userData: CreateUserData) => Promise<void>;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({ onClose, onCreate }) => {
  const { t } = useI18n();
  const [formData, setFormData] = useState<CreateUserData>({
    email: '',
    password: '',
    name: '',
    role: 'client',
    phone: '',
    document: '',
    birth_date: ''
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const validatePassword = (password: string): boolean => {
    if (password.length < 8) return false;
    if (!/\d/.test(password)) return false;
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) return false;
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validar senha
    if (!validatePassword(formData.password)) {
      setError('Senha deve ter no mínimo 8 caracteres, incluindo números e símbolos');
      return;
    }

    // Validar confirmação de senha
    if (formData.password !== confirmPassword) {
      setError(t('users.passwordMismatch'));
      return;
    }

    try {
      setSubmitting(true);
      await onCreate(formData);
    } catch (err: any) {
      setError(err.message || 'Erro ao criar usuário');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        width: '90%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <h2 style={{ marginTop: 0 }}>{t('createNewUser')}</h2>
        
        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            color: '#dc2626',
            marginBottom: '16px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
              Nome Completo *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <EmailInput
              label={t('common.email')}
              value={formData.email}
              onChange={(value) => setFormData({ ...formData, email: value })}
              required
            />
          </div>

          <PasswordInput
            label={t('login.passwordLabel')}
            value={formData.password}
            onChange={(value) => setFormData({ ...formData, password: value })}
            required
            showStrength
          />

          <PasswordInput
            label="Confirmar Senha"
            value={confirmPassword}
            onChange={setConfirmPassword}
            required
            showStrength={false}
          />

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
              Perfil *
            </label>
            <select
              required
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="client">{t('common.client')}</option>
              <option value="employee">{t('common.employee')}</option>
              <option value="supervisor">Supervisor</option>
              <option value="admin">{t('users.administrator')}</option>
            </select>
          </div>

          <PhoneInput
            label={t('common.phone')}
            value={formData.phone || ''}
            onChange={(value) => setFormData({ ...formData, phone: value })}
          />

          <DocumentInput
            label="Documento de Identificação"
            value={formData.document || ''}
            onChange={(value) => setFormData({ ...formData, document: value })}
            placeholder="Enter document ID"
          />

          <div style={{ marginBottom: '24px' }}>
            <DateInput
              label="Data de Nascimento"
              value={formData.birth_date || ''}
              onChange={(value) => setFormData({ ...formData, birth_date: value })}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >{t('actions.cancel')}</button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: '10px 20px',
                backgroundColor: submitting ? '#9ca3af' : '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {submitting ? 'Criando...' : 'Criar Usuário'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal de Edição de Usuário
interface EditUserModalProps {
  user: User;
  onClose: () => void;
  onUpdate: (userData: EditUserData) => Promise<void>;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ user, onClose, onUpdate }) => {
  const { t } = useI18n();
  const [formData, setFormData] = useState<EditUserData>({
    id: user.id,
    email: user.email,
    name: user.name || '',
    role: user.role,
    phone: user.phone || '',
    document: user.document || '',
    birth_date: user.birth_date || '',
    password: ''
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const validatePassword = (password: string): boolean => {
    if (password.length < 8) return false;
    if (!/\d/.test(password)) return false;
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) return false;
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Se está alterando a senha, validar
    if (showPasswordFields) {
      if (!formData.password) {
        setError('A senha é obrigatória quando "Alterar Senha" está marcado');
        return;
      }

      if (!validatePassword(formData.password)) {
        setError('Senha deve ter no mínimo 8 caracteres, incluindo números e símbolos');
        return;
      }

      if (formData.password !== confirmPassword) {
        setError(t('users.passwordMismatch'));
        return;
      }
    }

    // Se não está alterando senha, remover do objeto
    const dataToSend = { ...formData };
    if (!showPasswordFields) {
      delete dataToSend.password;
    }

    try {
      setSubmitting(true);
      await onUpdate(dataToSend);
    } catch (err: any) {
      setError(err.message || 'Erro ao atualizar usuário');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        width: '90%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <h2 style={{ marginTop: 0 }}>{t('editUser')}</h2>
        
        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            color: '#dc2626',
            marginBottom: '16px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
              Nome Completo *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <EmailInput
              label={t('common.email')}
              value={formData.email}
              onChange={(value) => setFormData({ ...formData, email: value })}
              required
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="user-role-edit-select" style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
              Perfil *
            </label>
            <select
              id="user-role-edit-select"
              name="role"
              required
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="client">{t('common.client')}</option>
              <option value="employee">{t('common.employee')}</option>
              <option value="supervisor">Supervisor</option>
              <option value="admin">{t('users.administrator')}</option>
            </select>
          </div>

          {/* Seção de Alteração de Senha */}
          <div style={{
            marginBottom: '16px',
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '6px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <input
                type="checkbox"
                id="changePassword"
                checked={showPasswordFields}
                onChange={(e) => {
                  setShowPasswordFields(e.target.checked);
                  if (!e.target.checked) {
                    setFormData({ ...formData, password: '' });
                    setConfirmPassword('');
                  }
                }}
                style={{ cursor: 'pointer' }}
              />
              <label htmlFor="changePassword" style={{ fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>{t('users.changePassword')}</label>
            </div>

            {showPasswordFields && (
              <>
                <PasswordInput
                  label="Nova Senha"
                  value={formData.password || ''}
                  onChange={(value) => setFormData({ ...formData, password: value })}
                  required={showPasswordFields}
                  showStrength
                />

                <PasswordInput
                  label="Confirmar Nova Senha"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  required={showPasswordFields}
                  showStrength={false}
                />
              </>
            )}
          </div>

          <PhoneInput
            label={t('common.phone')}
            value={formData.phone || ''}
            onChange={(value) => setFormData({ ...formData, phone: value })}
          />

          <DocumentInput
            label="Documento de Identificação"
            value={formData.document || ''}
            onChange={(value) => setFormData({ ...formData, document: value })}
            placeholder="Enter document ID"
          />

          <div style={{ marginBottom: '24px' }}>
            <DateInput
              label="Data de Nascimento"
              value={formData.birth_date || ''}
              onChange={(value) => setFormData({ ...formData, birth_date: value })}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >{t('actions.cancel')}</button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: '10px 20px',
                backgroundColor: submitting ? '#9ca3af' : '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {submitting ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Funções auxiliares
const getRoleColor = (role: string) => {
  const colors: Record<string, string> = {
    admin: '#ef4444',
    supervisor: '#f59e0b',
    employee: '#10b981',
    client: '#3b82f6'
  };
  return colors[role] || '#6b7280';
};

const getRoleLabel = (role: string) => {
  const labels: Record<string, string> = {
    admin: 'Administrador',
    supervisor: 'Supervisor',
    employee: 'Funcionário',
    client: 'Cliente'
  };
  return labels[role] || role;
};

